// Print an inventory transfer between two branches on A4.
// Mirrors the visual conventions of printPurchase.js.
import { API_BASE } from "@/lib/api";

const resolveImg = (u) => {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return API_BASE.replace(/\/api$/, "") + u;
};

const escape = (s) => {
  if (s == null) return "";
  return String(s)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
};

export function printTransfer(transfer, template, lang = "ar") {
  const isAR = lang === "ar";
  const dir = isAR ? "rtl" : "ltr";
  const t = (en, ar) => (isAR ? ar : en);
  const primary = template?.primary_color || "#D4AF64";

  const dateStr = (() => {
    try { return new Date(transfer.created_at).toLocaleString(isAR ? "ar-EG" : "en-US"); }
    catch { return transfer.created_at || ""; }
  })();

  const totalQty = (transfer.items || []).reduce((s, i) => s + (i.qty || 0), 0);

  const itemsRows = (transfer.items || []).map((it, idx) => {
    const name = isAR ? (it.name_ar || it.name_en) : (it.name_en || it.name_ar);
    return `
      <tr>
        <td class="num">${idx + 1}</td>
        <td>${escape(name || "")}</td>
        <td>${escape(it.sku || "—")}</td>
        <td class="num strong">${it.qty}</td>
      </tr>
    `;
  }).join("");

  const arrow = isAR ? "←" : "→";

  const html = `
<!DOCTYPE html>
<html lang="${isAR ? "ar" : "en"}" dir="${dir}">
<head>
<meta charset="UTF-8" />
<title>${escape(transfer.transfer_number || "")}</title>
<style>
  @page { size: A4; margin: 14mm; }
  * { box-sizing: border-box; }
  html, body {
    margin: 0; padding: 0;
    font-family: ${isAR ? "'IBM Plex Sans Arabic', sans-serif" : "'Inter', -apple-system, sans-serif"};
    color: #111; background: #fff; font-size: 11pt;
  }
  .page { width: 100%; max-width: 182mm; margin: 0 auto; padding: 8mm 0; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; padding-bottom: 12px; border-bottom: 2px solid ${primary}; }
  .brand { display: flex; gap: 12px; align-items: center; }
  .logo { width: 70px; height: 70px; object-fit: contain; }
  .biz h1 { margin: 0 0 4px 0; font-size: 20pt; color: ${primary}; }
  .biz .meta { font-size: 9pt; color: #555; line-height: 1.5; }
  .doc-info { text-align: ${isAR ? "left" : "right"}; }
  .doc-info .doc-title { font-size: 16pt; color: ${primary}; font-weight: 700; margin-bottom: 6px; }
  .doc-info .row { font-size: 10pt; color: #333; line-height: 1.6; }
  .doc-info .label { color: #777; margin-${isAR ? "left" : "right"}: 6px; }

  .route { display: flex; align-items: stretch; gap: 0; margin-top: 22px; border: 2px solid ${primary}; border-radius: 8px; overflow: hidden; }
  .route .leg { flex: 1; padding: 16px 18px; background: #fffaf0; }
  .route .leg.to { background: #f0fdf4; }
  .route .leg .lbl { font-size: 9pt; color: #888; text-transform: uppercase; letter-spacing: .5px; }
  .route .leg .branch { font-size: 16pt; color: #111; font-weight: 700; margin-top: 6px; }
  .route .arrow { display: flex; align-items: center; justify-content: center; width: 60px; background: ${primary}; color: #fff; font-size: 26pt; font-weight: 700; }

  .meta-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14px; margin-top: 18px; }
  .meta-box { border: 1px solid #ddd; padding: 10px 12px; border-radius: 4px; background: #fafafa; }
  .meta-box h3 { margin: 0 0 6px 0; font-size: 9pt; color: #666; text-transform: uppercase; letter-spacing: 0.5px; }
  .meta-box .v { font-size: 11pt; color: #111; line-height: 1.5; }

  table.items { width: 100%; border-collapse: collapse; margin-top: 18px; }
  table.items th { background: ${primary}; color: #fff; padding: 9px 10px; font-size: 10pt; text-align: ${isAR ? "right" : "left"}; font-weight: 600; }
  table.items td { padding: 9px 10px; font-size: 10.5pt; border-bottom: 1px solid #eee; }
  table.items td.num { text-align: ${isAR ? "left" : "right"}; font-variant-numeric: tabular-nums; }
  table.items td.strong { font-weight: 700; color: ${primary}; }
  table.items tr:nth-child(even) td { background: #fbfaf7; }

  .totals { margin-top: 12px; display: flex; justify-content: ${isAR ? "flex-start" : "flex-end"}; }
  .totals table { min-width: 280px; border-collapse: collapse; }
  .totals td { padding: 6px 10px; font-size: 11pt; }
  .totals .lbl { color: #555; }
  .totals .val { text-align: ${isAR ? "left" : "right"}; font-variant-numeric: tabular-nums; }
  .totals .grand { border-top: 2px solid ${primary}; font-weight: 700; font-size: 13pt; }
  .totals .grand .lbl, .totals .grand .val { color: ${primary}; }

  .notes { margin-top: 14px; font-size: 9.5pt; color: #555; padding: 10px 12px; background: #fafafa; border: 1px dashed #ccc; border-radius: 4px; white-space: pre-line; }

  .sig-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 30px; margin-top: 36px; }
  .sig-box { border-top: 1px solid #888; padding-top: 6px; font-size: 10pt; color: #444; text-align: center; }

  .footer { margin-top: 24px; padding-top: 14px; border-top: 1px dashed #aaa; font-size: 9pt; color: #888; text-align: center; }

  .actions { margin-top: 12px; text-align: center; padding-top: 14px; }
  .actions button { background: ${primary}; color: #fff; border: 0; padding: 10px 20px; border-radius: 6px; cursor: pointer; font-size: 11pt; }

  @media print {
    .actions { display: none; }
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
</style>
</head>
<body>
  <div class="page">
    <div class="header">
      <div class="brand">
        ${template?.logo_url ? `<img class="logo" src="${escape(resolveImg(template.logo_url))}" alt="" />` : ""}
        <div class="biz">
          <h1>${escape(isAR ? template?.business_name_ar : template?.business_name_en) || ""}</h1>
          <div class="meta">
            ${template?.phone ? `<div>${t("Phone", "هاتف")}: ${escape(template.phone)}</div>` : ""}
            ${template?.email ? `<div>${escape(template.email)}</div>` : ""}
            ${template?.tax_number ? `<div>${t("VAT", "الرقم الضريبي")}: ${escape(template.tax_number)}</div>` : ""}
          </div>
        </div>
      </div>
      <div class="doc-info">
        <div class="doc-title">${t("Stock Transfer", "تحويل مخزون")}</div>
        <div class="row"><span class="label">${t("Transfer #", "رقم التحويل")}:</span> <strong>${escape(transfer.transfer_number || "")}</strong></div>
        <div class="row"><span class="label">${t("Date", "التاريخ")}:</span> ${escape(dateStr)}</div>
        ${transfer.status ? `<div class="row"><span class="label">${t("Status", "الحالة")}:</span> ${escape(transfer.status)}</div>` : ""}
      </div>
    </div>

    <div class="route">
      <div class="leg from">
        <div class="lbl">${t("From Branch", "من الفرع")}</div>
        <div class="branch">${escape(transfer.from_branch_name || transfer.from_branch_id || "—")}</div>
      </div>
      <div class="arrow">${arrow}</div>
      <div class="leg to">
        <div class="lbl">${t("To Branch", "إلى الفرع")}</div>
        <div class="branch">${escape(transfer.to_branch_name || transfer.to_branch_id || "—")}</div>
      </div>
    </div>

    <div class="meta-grid">
      <div class="meta-box">
        <h3>${t("Items", "البنود")}</h3>
        <div class="v">${(transfer.items || []).length}</div>
      </div>
      <div class="meta-box">
        <h3>${t("Total Qty", "إجمالي الكمية")}</h3>
        <div class="v">${totalQty}</div>
      </div>
      <div class="meta-box">
        <h3>${t("Created By", "أنشأها")}</h3>
        <div class="v">${escape(transfer.created_by_name || transfer.created_by || "—")}</div>
      </div>
    </div>

    <table class="items">
      <thead>
        <tr>
          <th style="width:6%;">#</th>
          <th>${t("Product", "المنتج")}</th>
          <th style="width:20%;">${t("SKU", "الرمز")}</th>
          <th style="width:14%;">${t("Qty", "الكمية")}</th>
        </tr>
      </thead>
      <tbody>${itemsRows}</tbody>
    </table>

    <div class="totals">
      <table>
        <tr class="grand"><td class="lbl">${t("Total Qty", "إجمالي الكمية")}</td><td class="val">${totalQty}</td></tr>
      </table>
    </div>

    ${transfer.notes ? `<div class="notes"><strong>${t("Notes", "ملاحظات")}:</strong> ${escape(transfer.notes)}</div>` : ""}

    <div class="sig-grid">
      <div class="sig-box">${t("Sent by", "أرسلها")}</div>
      <div class="sig-box">${t("Received by", "استلمها")}</div>
      <div class="sig-box">${t("Approved by", "اعتمدها")}</div>
    </div>

    <div class="footer">
      ${t("This is an internal stock transfer document.", "هذه وثيقة تحويل مخزون داخلية.")}
    </div>

    <div class="actions">
      <button onclick="window.print()">${t("Print", "طباعة")}</button>
    </div>
  </div>
  <script>setTimeout(function(){ window.print(); }, 400);</script>
</body>
</html>
  `;

  const w = window.open("", "_blank", "width=900,height=1200");
  if (!w) return;
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  w.location.href = url;
  setTimeout(() => { try { URL.revokeObjectURL(url); } catch (e) { /* noop */ } }, 60_000);
}
